import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Checkbox, Modal, Radio, Space, Spin, Tag, Typography, message } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { getModulesList, activateModule, deactivateModule, setBillingTerm } from './api'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import { getLoadSessionAction } from '../../../store/session/action-creators'
import { fetchMenu } from '../../../store/user_menu/actions'
import {
  ApplyFooter,
  BenefitBlock,
  BenefitPercent,
  BenefitValue,
  DiscountBadge,
  ModuleActions,
  ModuleName,
  ModulePrice,
  ModuleRow,
  ModulesList,
  Page,
  PageHeader,
  PriceStrike,
  PricingLayout,
  PricingMain,
  SectionTitle,
  SummaryCard,
  SummaryLabel,
  SummaryLine,
  SummaryStrike,
  SummaryValue,
  SummaryValueLg,
  TermOption,
  TermRadio,
  TermToggle,
  TermToggleWrap,
} from './styles'

const { Title, Text } = Typography

interface ModulePriceInfo {
  module_code: string
  module_name?: string
  price_monthly: number
  base_total: number
  price_total: number
  price_monthly_effective: number
}

interface ModuleItem {
  code: string
  name: string
  description: string | null
  is_required: boolean
  status: string
  is_active: boolean
  activated_at: string | null
  scheduled_at: string | null
  scheduled_deactivation: boolean
  price_total: number
  price_monthly: number
}

interface VariantItem {
  term_months: number
  priority: number
  period_total: number
  base_period_total?: number
  price_monthly: number
  discount_percent?: number
  discount_badge?: string | null
  prices: ModulePriceInfo[]
}

function formatDate(value: string | null): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10)
  return d.toLocaleDateString('ru-RU')
}

function fmtRub(n: number) {
  return `${Math.round(Number(n || 0)).toLocaleString('ru-RU')} ₽`
}

function termLabel(months: number) {
  if (months === 1) return 'Базовый план (1 мес.)'
  if (months === 12) return '12 мес'
  return `${months} мес`
}

function shortTermLabel(months: number) {
  if (months === 1) return '1 мес'
  if (months === 12) return '12 мес'
  return `${months} мес`
}

function isBillable(m: ModuleItem) {
  return m.status === 'pending_next_period' || (m.status === 'active' && !m.scheduled_deactivation)
}

function syncDraftFromModules(list: ModuleItem[]) {
  return new Set(list.filter(m => m.is_required || isBillable(m)).map(m => m.code))
}

export default function ModulesPage() {
  const [modules, setModules] = useState<ModuleItem[]>([])
  const [variants, setVariants] = useState<VariantItem[]>([])
  const [billingTerm, setBillingTermState] = useState<number>(1)
  const [draftTerm, setDraftTerm] = useState<number>(1)
  const [draftCodes, setDraftCodes] = useState<Set<string>>(new Set())
  const [pendingTerm, setPendingTerm] = useState<number | null>(null)
  const [periodEnd, setPeriodEnd] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [confirmOpen, setConfirmOpen] = useState(false)
  const [actionMode, setActionMode] = useState<'now' | 'next_period'>('now')
  const [submitting, setSubmitting] = useState(false)
  const dispatch = useAppDispatch()

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getModulesList()
      const data = res.data || {}
      const term = Number(data.billing_term_months) || 1
      const list: ModuleItem[] = data.modules || []
      setModules(list)
      setVariants(data.variants || [])
      setBillingTermState(term)
      setDraftTerm(term)
      setDraftCodes(syncDraftFromModules(list))
      setPendingTerm(
        data.pending_billing_term_months != null ? Number(data.pending_billing_term_months) : null,
      )
      setPeriodEnd(data.current_period_end || null)
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить модули')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const displayVariants = useMemo(() => {
    const list = [...variants]
    if (!list.some(v => v.term_months === 1)) {
      const prices: ModulePriceInfo[] = modules.map(m => {
        const fromAny = list.flatMap(v => v.prices).find(p => p.module_code === m.code)
        const priceMonthly = Number(fromAny?.price_monthly ?? m.price_monthly) || 0
        return {
          module_code: m.code,
          module_name: m.name,
          price_monthly: priceMonthly,
          base_total: priceMonthly,
          price_total: priceMonthly,
          price_monthly_effective: priceMonthly,
        }
      })
      list.unshift({
        term_months: 1,
        priority: -1,
        discount_percent: 0,
        discount_badge: null,
        period_total: 0,
        price_monthly: 0,
        prices,
      })
    }
    return list.sort((a, b) => a.priority - b.priority || a.term_months - b.term_months)
  }, [variants, modules])

  const activeVariant = useMemo(
    () => displayVariants.find(v => v.term_months === draftTerm) || null,
    [displayVariants, draftTerm],
  )

  const priceByCode = useMemo(() => {
    const map = new Map<string, ModulePriceInfo>()
    for (const p of activeVariant?.prices || []) {
      map.set(p.module_code, p)
    }
    return map
  }, [activeVariant])

  const hasDiscount = Number(activeVariant?.discount_percent || 0) > 0

  const currentCodes = useMemo(
    () => new Set(modules.filter(m => m.is_required || isBillable(m)).map(m => m.code)),
    [modules],
  )

  const toActivate = useMemo(
    () => [...draftCodes].filter(code => !currentCodes.has(code)),
    [draftCodes, currentCodes],
  )

  const toDeactivate = useMemo(
    () =>
      [...currentCodes].filter(code => {
        const mod = modules.find(m => m.code === code)
        if (!mod || mod.is_required) return false
        return !draftCodes.has(code)
      }),
    [currentCodes, draftCodes, modules],
  )

  const termChanged = draftTerm !== billingTerm

  const hasChanges = termChanged || toActivate.length > 0 || toDeactivate.length > 0

  const summary = useMemo(() => {
    const selected = modules.filter(m => draftCodes.has(m.code))
    const prices = selected.map(m => priceByCode.get(m.code)).filter(Boolean) as ModulePriceInfo[]
    const periodTotal = prices.reduce((s, p) => s + p.price_total, 0)
    const basePeriodTotal = prices.reduce((s, p) => s + p.base_total, 0)
    const monthly = draftTerm > 0 ? Math.round((periodTotal / draftTerm) * 100) / 100 : 0

    const monthVariant = displayVariants.find(v => v.term_months === 1)
    const monthlyPlanBase = selected.reduce((sum, m) => {
      const fromMonth = monthVariant?.prices.find(p => p.module_code === m.code)
      if (fromMonth) return sum + Number(fromMonth.price_total || 0)
      const current = priceByCode.get(m.code)
      return sum + Number(current?.price_monthly || 0)
    }, 0)

    const savings = Math.max(0, Math.round((basePeriodTotal - periodTotal) * 100) / 100)
    const savingsPercent =
      basePeriodTotal > 0 && savings > 0 ? Math.round((100 * savings) / basePeriodTotal) : 0

    return { periodTotal, basePeriodTotal, monthly, monthlyPlanBase, savings, savingsPercent }
  }, [modules, draftCodes, priceByCode, draftTerm, displayVariants])

  const scheduledDeactivations = useMemo(
    () => modules.filter(m => m.scheduled_deactivation),
    [modules],
  )
  const pendingActivations = useMemo(
    () => modules.filter(m => m.status === 'pending_next_period'),
    [modules],
  )

  const toggleModule = (code: string, checked: boolean) => {
    const mod = modules.find(m => m.code === code)
    if (!mod || mod.is_required) return
    setDraftCodes(prev => {
      const next = new Set(prev)
      if (checked) next.add(code)
      else next.delete(code)
      return next
    })
  }

  const resetDraft = () => {
    setDraftTerm(billingTerm)
    setDraftCodes(syncDraftFromModules(modules))
  }

  const openConfirm = () => {
    if (!hasChanges) return
    setActionMode('now')
    setConfirmOpen(true)
  }

  const applyChanges = useCallback(async () => {
    setSubmitting(true)
    try {
      if (termChanged) {
        await setBillingTerm(draftTerm, actionMode)
      }
      for (const code of toActivate) {
        await activateModule(code, actionMode)
      }
      for (const code of toDeactivate) {
        await deactivateModule(code, actionMode)
      }
      message.success('Изменения применены')
      setConfirmOpen(false)
      await load()
      // Роуты (requiredModule), меню и права зависят от activeModules в сессии —
      // без перезагрузки сессии подключённый раздел остаётся «Модуль не подключён» до F5.
      await dispatch(getLoadSessionAction() as any)
      dispatch(fetchMenu() as any)
    } catch (e: any) {
      message.error(e?.message || 'Не удалось применить изменения')
    } finally {
      setSubmitting(false)
    }
  }, [termChanged, draftTerm, actionMode, toActivate, toDeactivate, load, dispatch])

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
      </div>
    )
  }

  return (
    <Page>
      <PageHeader>
        <Title level={4} style={{ marginBottom: 4 }}>
          Модули и тариф
        </Title>
        <Text type="secondary">
          Выберите срок и модули — цены пересчитаются сразу. Подключение одной кнопкой снизу.
          {periodEnd ? ` Текущий период до ${formatDate(periodEnd)}.` : ''}
        </Text>
      </PageHeader>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      {pendingTerm != null && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Смена тарифа на «${termLabel(pendingTerm)}» запланирована на конец периода`}
        />
      )}

      {scheduledDeactivations.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Запланировано отключение модулей"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {scheduledDeactivations.map(m => (
                <li key={m.code}>
                  «{m.name}» — {formatDate(m.scheduled_at)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {pendingActivations.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Запланировано подключение модулей"
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {pendingActivations.map(m => (
                <li key={m.code}>
                  «{m.name}» — {formatDate(m.scheduled_at)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <PricingLayout>
        <PricingMain>
          {displayVariants.length > 0 && (
            <>
              <SectionTitle>Срок подписки</SectionTitle>
              <TermToggleWrap>
                <TermToggle>
                  {displayVariants.map(v => {
                    const active = draftTerm === v.term_months
                    return (
                      <TermOption
                        key={v.term_months}
                        type="button"
                        $active={active}
                        onClick={() => setDraftTerm(v.term_months)}
                      >
                        <TermRadio $active={active} />
                        {termLabel(v.term_months)}
                        {v.discount_badge ? (
                          <DiscountBadge>{v.discount_badge}</DiscountBadge>
                        ) : null}
                        {v.term_months === billingTerm ? (
                          <Tag color="green" style={{ margin: 0, fontSize: 11 }}>
                            текущий
                          </Tag>
                        ) : null}
                      </TermOption>
                    )
                  })}
                </TermToggle>
              </TermToggleWrap>
            </>
          )}

          <SectionTitle>Модули</SectionTitle>
          <ModulesList>
            {modules.map(mod => {
              const price = priceByCode.get(mod.code)
              const baseMonthly = price?.price_monthly ?? mod.price_monthly
              const effectiveMonthly = price?.price_monthly_effective ?? mod.price_monthly
              const checked = draftCodes.has(mod.code)
              const live = isBillable(mod)

              return (
                <ModuleRow
                  key={mod.code}
                  $clickable={!mod.is_required}
                  onClick={() => {
                    if (mod.is_required) return
                    toggleModule(mod.code, !checked)
                  }}
                >
                  <ModuleName>
                    <Checkbox
                      checked={checked}
                      disabled={mod.is_required}
                      onClick={e => e.stopPropagation()}
                      onChange={e => toggleModule(mod.code, e.target.checked)}
                    />
                    {mod.name}
                    {live && <Tag color="green">Активен</Tag>}
                    {mod.status === 'pending_next_period' && (
                      <Tag color="blue">С нового периода</Tag>
                    )}
                    {mod.scheduled_deactivation && (
                      <Tag color="orange">До {formatDate(mod.scheduled_at)}</Tag>
                    )}
                    {mod.is_required && <Tag>Обязательный</Tag>}
                  </ModuleName>
                  <ModulePrice>
                    {hasDiscount && checked ? (
                      <>
                        <PriceStrike>{fmtRub(baseMonthly)}</PriceStrike>
                        {fmtRub(effectiveMonthly)}
                      </>
                    ) : (
                      fmtRub(baseMonthly)
                    )}
                  </ModulePrice>
                  <ModuleActions>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {checked ? 'в расчёте' : 'не выбран'}
                    </Text>
                  </ModuleActions>
                </ModuleRow>
              )
            })}
            {!modules.length && (
              <div style={{ padding: 16 }}>
                <Text type="secondary">Модули не найдены</Text>
              </div>
            )}
          </ModulesList>
        </PricingMain>

        <SummaryCard>
          <SummaryLine>
            <SummaryLabel>Базовый план (1 мес.)</SummaryLabel>
            <SummaryValue>{fmtRub(summary.monthlyPlanBase)}</SummaryValue>
          </SummaryLine>
          {draftTerm > 1 && (
            <SummaryLine>
              <SummaryLabel>Стоимость в месяц по тарифу</SummaryLabel>
              <SummaryValue>
                {hasDiscount && summary.monthlyPlanBase > summary.monthly ? (
                  <SummaryStrike>{fmtRub(summary.monthlyPlanBase)}</SummaryStrike>
                ) : null}
                {fmtRub(summary.monthly)}
              </SummaryValue>
            </SummaryLine>
          )}
          <SummaryLine>
            <SummaryLabel>
              Итого к оплате{draftTerm > 1 ? ` за ${shortTermLabel(draftTerm)}` : ''}
            </SummaryLabel>
            <SummaryValueLg>
              {hasDiscount && summary.basePeriodTotal > summary.periodTotal ? (
                <SummaryStrike>{fmtRub(summary.basePeriodTotal)}</SummaryStrike>
              ) : null}
              {fmtRub(summary.periodTotal)}
            </SummaryValueLg>
          </SummaryLine>
          {summary.savings > 0 && (
            <BenefitBlock>
              <SummaryLabel>Выгода</SummaryLabel>
              <BenefitValue>
                {fmtRub(summary.savings)}
                {summary.savingsPercent > 0 ? (
                  <BenefitPercent>−{summary.savingsPercent}%</BenefitPercent>
                ) : null}
              </BenefitValue>
              <Text type="secondary" style={{ fontSize: 12 }}>
                относительно базового плана на {shortTermLabel(draftTerm)}
              </Text>
            </BenefitBlock>
          )}
        </SummaryCard>
      </PricingLayout>

      {hasChanges ? (
        <ApplyFooter>
          <div>
            <Text type="secondary">
              {termChanged ? `Тариф → ${shortTermLabel(draftTerm)}. ` : ''}
              {toActivate.length > 0 ? `Подключить: ${toActivate.length}. ` : ''}
              {toDeactivate.length > 0 ? `Отключить: ${toDeactivate.length}.` : ''}
            </Text>
          </div>
          <Space>
            <Button disabled={!hasChanges || submitting} onClick={resetDraft}>
              Сбросить
            </Button>
            <Button
              type="primary"
              disabled={!hasChanges}
              loading={submitting}
              onClick={openConfirm}
            >
              Подключить
            </Button>
          </Space>
        </ApplyFooter>
      ) : null}
      <Modal
        open={confirmOpen}
        title="Подтверждение подключения"
        onCancel={() => setConfirmOpen(false)}
        onOk={applyChanges}
        okText="Подключить"
        confirmLoading={submitting}
        cancelText="Отмена"
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          {termChanged && (
            <Text>
              Тариф: {shortTermLabel(billingTerm)} → {shortTermLabel(draftTerm)}
            </Text>
          )}
          {toActivate.length > 0 && (
            <Text>
              Подключить:{' '}
              {toActivate.map(code => modules.find(m => m.code === code)?.name || code).join(', ')}
            </Text>
          )}
          {toDeactivate.length > 0 && (
            <Text>
              Отключить:{' '}
              {toDeactivate
                .map(code => modules.find(m => m.code === code)?.name || code)
                .join(', ')}
            </Text>
          )}
          {periodEnd ? (
            <Radio.Group value={actionMode} onChange={e => setActionMode(e.target.value)}>
              <Space direction="vertical">
                <Radio value="now">Применить сейчас</Radio>
                <Radio value="next_period">С конца / начала следующего периода</Radio>
              </Space>
            </Radio.Group>
          ) : null}
          <Text type="secondary">
            Итого по выбранной конфигурации: {fmtRub(summary.periodTotal)}
            {draftTerm > 1 ? ` за ${shortTermLabel(draftTerm)}` : ''}
          </Text>
        </Space>
      </Modal>
    </Page>
  )
}
